import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import ModalViewModel from 'infra/ModalViewModel'
import { AlertFactory } from 'alerts/Alert'
import { Bundle } from 'admin/bundle/bundle'
import { BundleStore } from 'admin/bundle/BundleStore'
import { RefreshBundles } from 'admin/bundle/BundleSearchViewModel'
import {
  formatBundleNames,
  pluralizeBundles,
  showBulkActionError,
  showBulkActionResult,
} from 'admin/bundle/BulkActionAlerts'
import { Id } from 'types/definitions'

export class BulkEditPriceViewModel extends ModalViewModel {
  @observable
  selectedBundles: Bundle[] = []

  /**
   * The new price entered per bundle. A bundle without an entry is left
   * unchanged; an entry of 0 sets the bundle to $0.
   */
  @observable
  newPrices = new Map<Id, number>()

  constructor(
    private bundleStore: BundleStore,
    private newAlert: AlertFactory,
    private refreshBundles: RefreshBundles,
  ) {
    super()
    makeObservable(this)

    this.close$.subscribe(this.reset)
  }

  /**
   * Archived bundles can't be repriced, so they never appear in the editor.
   */
  @computed
  get eligibleBundles() {
    return this.selectedBundles.filter((bundle) => !bundle.archived)
  }

  @computed
  get archivedCount() {
    return this.selectedBundles.length - this.eligibleBundles.length
  }

  @computed
  get hasEligibleBundles() {
    return this.eligibleBundles.length > 0
  }

  @computed
  get hasArchivedBundles() {
    return this.archivedCount > 0
  }

  /**
   * The bundles the user gave a new price. Blank rows are skipped; a $0 entry
   * is a real edit.
   */
  @computed
  get edits() {
    return this.eligibleBundles.flatMap((bundle) => {
      const price = this.newPrices.get(bundle.Id)
      return price === undefined ? [] : [{ bundle, price }]
    })
  }

  @computed
  get canApply() {
    return this.edits.length > 0
  }

  priceFor(bundleId: Id): number | null {
    return this.newPrices.get(bundleId) ?? null
  }

  @action.bound
  setPrice(bundleId: Id, price: number | null) {
    if (price === null) {
      this.newPrices.delete(bundleId)
    } else {
      this.newPrices.set(bundleId, price)
    }
  }

  @action
  open(bundles: Bundle[]) {
    this.reset()
    this.selectedBundles = bundles

    this.show()
  }

  @action.bound
  reset() {
    this.selectedBundles = []
    this.newPrices.clear()

    this.submit.reset()
  }

  submit = task.resolved(async () => {
    const edits = this.edits

    if (edits.length === 0) {
      return
    }

    try {
      const result = await this.bundleStore.bulkEditPrices(
        Id.assert(edits[0].bundle.workspace.id),
        edits.map((edit) => ({
          bundleId: Id.assert(edit.bundle.Id),
          price: edit.price,
        })),
      )

      const skippedIds = new Set<Id>([
        ...result.notFoundIds,
        ...result.archivedIds,
      ])

      showBulkActionResult(
        this.newAlert,
        edits.map((edit) => edit.bundle),
        skippedIds,
        {
          success: (count) =>
            `Updated the price of ${count} ${pluralizeBundles(count)}.`,
          partialTitle: 'Some bundles were skipped',
          partial: (count, skipped) =>
            `Updated ${count} of ${count + skipped.length} bundles. Skipped: ${formatBundleNames(skipped)}.`,
        },
      )
    } catch (e) {
      showBulkActionError(
        this.newAlert,
        e,
        'You do not have permission to edit prices in this workspace.',
        'There was an error updating the prices.',
      )
      this.close()
      return
    }

    await this.refreshBundles(edits[0].bundle.workspace)

    this.close()
  })
}
